import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useSitesPageStore } from "./sitesPageStore/sitesPageStore";
import { NavigateFunction } from "react-router-dom";
import { OrangeTextButton } from "../../../common/OrangeTextButton";

interface TenantSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  navigate: NavigateFunction;
}

export const TenantSettingsDialog: React.FC<TenantSettingsDialogProps> = ({
  open,
  onClose,
  navigate,
}) => {
  const {
    batchSize,
    minimumResolutionHeight,
    fetchingTenantSettings,
    handlePatchTenantSettings,
    setUpdatingBatchSize,
    selectedGroupName,
  } = useSitesPageStore();

  const [localBatchSize, setLocalBatchSize] = React.useState<number | string>(
    batchSize ?? 0
  );

  const [localMinimumResolutionHeight, setLocalMinimumResolutionHeight] =
    React.useState<number | string>(minimumResolutionHeight ?? 0);

  const [hasChanges, setHasChanges] = React.useState(false);

  // Reset local state when dialog opens
  React.useEffect(() => {
    if (open) {
      setLocalBatchSize(batchSize ?? 0);
      setLocalMinimumResolutionHeight(minimumResolutionHeight ?? 0);
    }
  }, [open, batchSize, minimumResolutionHeight]);

  // Check if the current value is different from the original

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {selectedGroupName
          ? `${selectedGroupName}`
          : "View / Edit Tenant Settings"}
      </DialogTitle>

      {fetchingTenantSettings ? (
        <div className="flex justify-center items-center h-full">
          <CircularProgress color="primary" />
        </div>
      ) : (
        <div>
          <DialogContent sx={{ width: 400 }}>
            <div className="pl-[20px]">
              <Typography>Max Batch Size:</Typography>
              <TextField
                id="outlined-basic"
                variant="outlined"
                type="number"
                value={localBatchSize}
                inputProps={{
                  min: 1,
                  step: 1,
                }}
                sx={{ width: 150 }}
                onChange={(event) => {
                  const value = event.target.value;
                  // Only allow positive integers
                  if (value === "" || /^\d+$/.test(value)) {
                    const numericValue = value === "" ? "" : Number(value);
                    setLocalBatchSize(numericValue);
                    setHasChanges(true);
                  }
                }}
                onKeyDown={(event) => {
                  // Prevent non-numeric characters except backspace, delete, arrow keys, etc.
                  const allowedKeys = [
                    "Backspace",
                    "Delete",
                    "ArrowLeft",
                    "ArrowRight",
                    "Tab",
                  ];
                  if (
                    !allowedKeys.includes(event.key) &&
                    !/^\d$/.test(event.key)
                  ) {
                    event.preventDefault();
                  }
                }}
              />
              <div>
                <Typography sx={{ paddingTop: "20px" }}>
                  Minimum Resolution Height:
                </Typography>
                <TextField
                  value={localMinimumResolutionHeight}
                  onChange={(event: any) => {
                    const value = event.target.value;
                    // Only allow positive integers
                    if (value === "" || /^\d+$/.test(value)) {
                      const numericValue = value === "" ? "" : Number(value);
                      setLocalMinimumResolutionHeight(numericValue);
                      setHasChanges(true);
                    }
                  }}
                  error={Number(localMinimumResolutionHeight) < 360}
                  helperText={
                    Number(localMinimumResolutionHeight) < 360
                      ? "Any resolution below 360 px may experience poor scene change performance."
                      : ""
                  }
                  variant="outlined"
                  sx={{ width: 150 }}
                  type="number"
                  inputProps={{ min: 0 }}
                  InputLabelProps={{ shrink: true }}
                  autoComplete="off"
                />
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <OrangeTextButton
              onClick={() => {
                onClose();
              }}
              text="Cancel"
              disabled={false}
            />

            <OrangeTextButton
              onClick={() => {
                setUpdatingBatchSize(true);
                handlePatchTenantSettings(
                  Number(localBatchSize),
                  Number(localMinimumResolutionHeight),
                  navigate
                );
                onClose();
              }}
              text={` Save `}
              disabled={!hasChanges}
            />
          </DialogActions>
        </div>
      )}
    </Dialog>
  );
};
